import {
  haptic,
  springs,
  MoonPressable,
  Dialog,
  DialogPrimaryButton,
  MoonSkeleton,
  SectionHeader,
} from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useState, useEffect, useMemo, useRef } from "react";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import {
  View,
  Text,
  Pressable,
  Image,
  Platform,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  withDelay,
  runOnJS,
} from "react-native-reanimated";
import ViewAllLink from "../../../components/ViewAllLink";
import { EMBER } from "../../../../onboarding/theme";
import type { Badge } from "../types";
import type { BadgeUnlockEvent } from "@src/packages/badges/badge-events";

const waxSealImage = require("../../../../assets/images/wax-seal.png");

const BADGE_IMAGES: Record<string, ReturnType<typeof require>> = {
  "first-steps": require("../../../../assets/images/passport-badges/first-steps.png"),
  "profile-complete": require("../../../../assets/images/passport-badges/profile-complete.png"),
  "photo-day": require("../../../../assets/images/passport-badges/photo-day.png"),
  "insurance-navigator": require("../../../../assets/images/passport-badges/insurance-navigator.png"),
  "clinic-finder": require("../../../../assets/images/passport-badges/clinic-finder.png"),
  "treatment-decided": require("../../../../assets/images/passport-badges/treatment-decided.png"),
  "phone-verified": require("../../../../assets/images/passport-badges/phone-verified.png"),
  "lab-explorer": require("../../../../assets/images/badge-microscope.png"),
  "appointment-booked": require("../../../../assets/images/passport-badges/appointment-booked.png"),
  "clinic-day": require("../../../../assets/images/passport-badges/clinic-day.png"),
  "protocol-pioneer": require("../../../../assets/images/passport-badges/protocol-pioneer.png"),
  "first-retrieval": require("../../../../assets/images/passport-badges/first-retrieval.png"),
  "embryo-update": require("../../../../assets/images/badge-sperm.png"),
  "transfer-day": require("../../../../assets/images/passport-badges/transfer-day.png"),
  "the-wait": require("../../../../assets/images/passport-badges/the-wait.png"),
  consistent: require("../../../../assets/images/passport-badges/consistent.png"),
  "supplement-starter": require("../../../../assets/images/passport-badges/supplement-starter.png"),
  "medication-streak-3": require("../../../../assets/images/passport-badges/medication-streak-3.png"),
  "medication-streak-7": require("../../../../assets/images/passport-badges/medication-streak-7.png"),
  "med-tracker": require("../../../../assets/images/passport-badges/med-tracker.png"),
  "hydration-hero": require("../../../../assets/images/passport-badges/hydration-hero.png"),
  "hydration-streak-3": require("../../../../assets/images/passport-badges/hydration-streak-3.png"),
  "mindful-moment": require("../../../../assets/images/passport-badges/mindful-moment.png"),
  "first-article": require("../../../../assets/images/passport-badges/first-article.png"),
  "knowledge-seeker": require("../../../../assets/images/passport-badges/knowledge-seeker.png"),
  "deep-diver": require("../../../../assets/images/passport-badges/deep-diver.png"),
  "myth-buster": require("../../../../assets/images/passport-badges/myth-buster.png"),
  "question-master": require("../../../../assets/images/passport-badges/question-master.png"),
  "resource-collector": require("../../../../assets/images/passport-badges/resource-collector.png"),
  "first-resource": require("../../../../assets/images/passport-badges/first-resource.png"),
  "informed-consent": require("../../../../assets/images/passport-badges/informed-consent.png"),
  "research-reader": require("../../../../assets/images/passport-badges/research-reader.png"),
  connector: require("../../../../assets/images/passport-badges/connector.png"),
  "data-owner": require("../../../../assets/images/passport-badges/data-owner.png"),
  "story-sharer": require("../../../../assets/images/passport-badges/story-sharer.png"),
  "support-circle": require("../../../../assets/images/passport-badges/support-circle.png"),
  "buddy-system": require("../../../../assets/images/passport-badges/buddy-system.png"),
  "feedback-champion": require("../../../../assets/images/passport-badges/feedback-champion.png"),
  ambassador: require("../../../../assets/images/passport-badges/ambassador.png"),
  "one-month-in": require("../../../../assets/images/passport-badges/one-month-in.png"),
  "quarter-mark": require("../../../../assets/images/passport-badges/quarter-mark.png"),
  "halfway-there": require("../../../../assets/images/passport-badges/halfway-there.png"),
  "full-circle": require("../../../../assets/images/passport-badges/full-circle.png"),
};

function getBadgeImage(id: string) {
  return BADGE_IMAGES[id] ?? waxSealImage;
}

const SEAL_SIZE = 56;

type Props = {
  badges: Badge[];
  badgeDataStatus: "loading" | "ready" | "unavailable";
  initialBadgeId?: string;
  initialBadgeIds?: string;
};

function PassportLoadingSkeleton({ t }: { t: (key: string) => string }) {
  return (
    <View style={{ marginBottom: 20 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <SectionHeader style={{ marginBottom: 0 }}>
          {t("you.my_passport.header")}
        </SectionHeader>
      </View>
      <View
        style={{
          ...GLASS_CARD_STYLE,
          minHeight: 88,
          paddingHorizontal: 16,
          paddingVertical: 16,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        {[0, 1, 2, 3].map((index) => (
          <MoonSkeleton key={index} width={48} height={48} circle />
        ))}
      </View>
    </View>
  );
}

function PassportUnavailableCard({ t }: { t: (key: string) => string }) {
  return (
    <View style={{ marginBottom: 20 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <SectionHeader style={{ marginBottom: 0 }}>
          {t("you.my_passport.header")}
        </SectionHeader>
      </View>
      <View
        style={{
          ...GLASS_CARD_STYLE,
          minHeight: 84,
          paddingHorizontal: 20,
          paddingVertical: 18,
          justifyContent: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.textLight,
          }}
        >
          {t("you.my_passport.unavailable")}
        </Text>
      </View>
    </View>
  );
}

function InlineSeal({
  badge,
  onPress,
  isNewlyUnlocked,
}: {
  badge: Badge;
  onPress: () => void;
  isNewlyUnlocked: boolean;
}) {
  const unlocked = badge.unlocked;
  const scale = useSharedValue(isNewlyUnlocked ? 0 : 1);
  const glow = useSharedValue(0);

  useEffect(() => {
    if (isNewlyUnlocked) {
      scale.value = withDelay(
        200,
        withSpring(1, { damping: 8, stiffness: 200 }, (finished) => {
          if (finished) runOnJS(haptic)("nudge");
        }),
      );
      glow.value = withDelay(
        200,
        withTiming(1, { duration: 400 }, () => {
          glow.value = withTiming(0, { duration: 600 });
        }),
      );
    }
  }, [isNewlyUnlocked, scale, glow]);

  const animStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const glowStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    width: SEAL_SIZE + 12,
    height: SEAL_SIZE + 12,
    borderRadius: (SEAL_SIZE + 12) / 2,
    backgroundColor: `rgba(255, 193, 7, ${glow.value * 0.35})`,
  }));

  return (
    <MoonPressable
      onPress={onPress}
      haptic="light"
      activeScale={0.92}
      style={{ alignItems: "center", marginRight: 14 }}
    >
      <View
        style={{
          width: SEAL_SIZE,
          height: SEAL_SIZE,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isNewlyUnlocked && <Animated.View style={glowStyle} />}
        <Animated.View style={animStyle}>
          <Image
            source={getBadgeImage(badge.id)}
            style={{
              width: SEAL_SIZE,
              height: SEAL_SIZE,
              ...(unlocked
                ? {}
                : Platform.OS === "web"
                  ? { opacity: 0.5, filter: "grayscale(1) brightness(1.1)" }
                  : { opacity: 0.3 }),
            }}
            resizeMode="contain"
          />
        </Animated.View>
      </View>
    </MoonPressable>
  );
}

// ── Animated seal for the detail panel ──

function fireHapticAtPeak() {
  haptic("nudge");
}

function AnimatedSeal({
  badge,
  reducedMotion = false,
  celebrationEffectsEnabled = true,
}: {
  badge: Badge;
  reducedMotion?: boolean;
  celebrationEffectsEnabled?: boolean;
}) {
  const scale = useSharedValue(0.3);
  const rotate = useSharedValue(-15);
  const opacity = useSharedValue(0);

  const PANEL_SEAL = 140;

  useEffect(() => {
    if (reducedMotion) {
      scale.value = 1;
      rotate.value = 0;
      opacity.value = 1;
      return;
    }

    scale.value = 0.3;
    rotate.value = -15;
    opacity.value = 0;

    // Fade in first
    opacity.value = withTiming(1, { duration: 150 });

    // Spring scale with haptic at peak
    scale.value = withDelay(
      150,
      withSpring(1, springs.bounce, (finished) => {
        if (finished && celebrationEffectsEnabled) {
          runOnJS(fireHapticAtPeak)();
        }
      }),
    );

    // Rotate back to 0
    rotate.value = withDelay(150, withTiming(0, { duration: 500 }));
  }, [
    badge.id,
    celebrationEffectsEnabled,
    reducedMotion,
    scale,
    rotate,
    opacity,
  ]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }, { rotate: `${rotate.value}deg` }],
  }));

  return (
    <Animated.View
      style={[
        {
          alignItems: "center",
          justifyContent: "center",
          width: PANEL_SEAL,
          height: PANEL_SEAL,
        },
        animatedStyle,
      ]}
    >
      <Image
        source={getBadgeImage(badge.id)}
        style={{ width: PANEL_SEAL, height: PANEL_SEAL, position: "absolute" }}
        resizeMode="contain"
      />
    </Animated.View>
  );
}

// ── Badge detail panel ──

function BadgeDetailPanel({
  badge,
  visible,
  onClose,
}: {
  badge: Badge | null;
  visible: boolean;
  onClose: () => void;
}) {
  if (!badge) return null;

  return (
    <Dialog visible={visible} onClose={onClose} closeButton="minimal">
      <View style={{ alignItems: "center" }}>
        <BadgeDetailContent badge={badge} />
      </View>
    </Dialog>
  );
}

export function BadgeUnlockDialog({
  badge,
  event,
  visible,
  onClose,
  onViewInPassport,
  reducedMotion = false,
  celebrationEffectsEnabled = true,
}: {
  badge: Badge | null;
  event?: BadgeUnlockEvent;
  visible: boolean;
  onClose: () => void;
  onViewInPassport?: () => void;
  reducedMotion?: boolean;
  celebrationEffectsEnabled?: boolean;
}) {
  if (!badge) return null;

  return (
    <Dialog visible={visible} onClose={onClose} closeButton="minimal">
      <View style={{ alignItems: "center" }}>
        <BadgeDetailContent
          badge={badge}
          event={event}
          isNewlyUnlocked
          onViewInPassport={onViewInPassport}
          reducedMotion={reducedMotion}
          celebrationEffectsEnabled={celebrationEffectsEnabled}
        />
      </View>
    </Dialog>
  );
}

/**
 * Several badges at once, as one thing to read rather than a queue to clear.
 *
 * Badges used to arrive one dialog at a time: close, another opens, close
 * again. A patient who had earned four was made to dismiss four modals before
 * she could get back to what she was doing, and the celebration turned into
 * paperwork. Awarding on the action makes long pile-ups rare, but not
 * impossible — one save can complete a profile and add its photo, and the first
 * load after a release still catches up everything earned before the rules
 * existed.
 *
 * So: one dialog, swipe between them, one close that acknowledges the lot. The
 * card is the same `BadgeDetailContent` the single-badge dialog shows, at the
 * dialog's own width, so nothing about a badge looks different for having
 * arrived with company.
 */
export function BadgeUnlockCarouselDialog({
  badges,
  visible,
  onClose,
  onViewInPassport,
  reducedMotion = false,
  celebrationEffectsEnabled = true,
}: {
  badges: { badge: Badge; event?: BadgeUnlockEvent }[];
  visible: boolean;
  onClose: () => void;
  onViewInPassport?: (badgeId: string) => void;
  reducedMotion?: boolean;
  celebrationEffectsEnabled?: boolean;
}) {
  const t = useTranslations();
  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * One card per badge, and a signature rather than a count.
   *
   * The provider groups pending unlocks by event id, so one badge unlocked
   * twice arrives twice — which would render the same card twice, claim one
   * badge too many in the heading, and collide on its React key. And a
   * different set of the same size is still a different set, so resetting on
   * `length` would leave the dots naming a card that is not on screen.
   */
  const uniqueBadges = useMemo<
    { badge: Badge; event?: BadgeUnlockEvent }[]
  >(() => {
    const seen = new Set<string>();
    return badges.filter((item) => {
      if (seen.has(item.badge.id)) return false;
      seen.add(item.badge.id);
      return true;
    });
  }, [badges]);
  const signature = uniqueBadges.map((item) => item.badge.id).join("|");

  useEffect(() => {
    // A new pile-up starts at its first badge, not wherever the last one ended.
    setActiveIndex(0);
  }, [signature]);

  if (uniqueBadges.length === 0) return null;

  return (
    <Dialog visible={visible} onClose={onClose} closeButton="minimal">
      <View style={{ alignItems: "center", gap: 12 }}>
        <Text
          accessibilityRole="header"
          style={{
            fontSize: 12,
            fontWeight: "800",
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: EMBER.colors.textLight,
          }}
        >
          {t("you.my_passport.badges_unlocked_together", {
            params: { count: String(uniqueBadges.length) },
          })}
        </Text>

        <HorizontalSnapCarousel
          items={uniqueBadges}
          gap={0}
          getCardWidth={(containerWidth) => containerWidth}
          keyExtractor={(item) => item.event?.eventId ?? item.badge.id}
          onActiveIndexChange={setActiveIndex}
          style={{ alignSelf: "stretch" }}
          /**
           * Heights are left alone. A horizontal row already stretches its
           * children to the tallest, and forcing that here made one badge with
           * long copy set the height of all of them — enough, in a dialog with
           * no vertical scroll, to push "View in passport" off the bottom with
           * no way to reach it.
           */
          renderItem={(item, cardWidth, index) => (
            <View style={{ width: cardWidth, alignItems: "center" }}>
              <BadgeDetailContent
                badge={item.badge}
                event={item.event}
                /**
                 * Newly unlocked only when an unlock event came with it. The
                 * same dialog is reached from the passport after the
                 * celebration has been acknowledged, and there these are badges
                 * she owns rather than news being broken to her.
                 */
                isNewlyUnlocked={Boolean(item.event)}
                onViewInPassport={
                  onViewInPassport
                    ? () => onViewInPassport(item.badge.id)
                    : undefined
                }
                reducedMotion={reducedMotion}
                /**
                 * The first card celebrates, once, when the dialog opens.
                 *
                 * Keying this to "the card on screen" looked right and was not:
                 * the seal fires a haptic when its entrance spring settles, and
                 * its effect depends on this prop — so the flag flipping on two
                 * cards per swipe replayed the animation and buzzed again on
                 * every crossing, indefinitely. Worse, on the passport-arrival
                 * path no card carries an event, so comparing event ids was
                 * `undefined === undefined` and every card celebrated at once:
                 * three impacts in a few milliseconds, the exact burst this
                 * dialog exists to stop. Swiping is reading, not news.
                 */
                celebrationEffectsEnabled={
                  celebrationEffectsEnabled && index === 0
                }
              />
            </View>
          )}
        />

        <View
          accessibilityRole="text"
          accessibilityLabel={t("you.my_passport.badge_position", {
            params: {
              position: String(activeIndex + 1),
              count: String(uniqueBadges.length),
            },
          })}
          style={{ flexDirection: "row", gap: 6, alignItems: "center" }}
        >
          {uniqueBadges.map((item, index) => (
            <View
              key={item.event?.eventId ?? item.badge.id}
              style={{
                width: index === activeIndex ? 18 : 6,
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  index === activeIndex
                    ? EMBER.colors.text
                    : EMBER.colors.textLight,
                opacity: index === activeIndex ? 1 : 0.4,
              }}
            />
          ))}
        </View>
      </View>
    </Dialog>
  );
}

function BadgeDetailContent({
  badge,
  event,
  isNewlyUnlocked = false,
  onViewInPassport,
  reducedMotion = false,
  celebrationEffectsEnabled = true,
}: {
  badge: Badge;
  event?: BadgeUnlockEvent;
  isNewlyUnlocked?: boolean;
  onViewInPassport?: () => void;
  reducedMotion?: boolean;
  celebrationEffectsEnabled?: boolean;
}) {
  const t = useTranslations();
  const { long: formatDate } = useDateFormat();
  return (
    <>
      <AnimatedSeal
        badge={badge}
        reducedMotion={reducedMotion}
        celebrationEffectsEnabled={celebrationEffectsEnabled}
      />

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: EMBER.colors.text,
          marginTop: 16,
          textAlign: "center",
        }}
      >
        {event?.copy.title ?? badge.name}
      </Text>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textLight,
          textAlign: "center",
          lineHeight: 20,
          marginTop: 8,
          paddingHorizontal: 8,
        }}
      >
        {event?.copy.body ?? (badge.unlocked ? badge.description : badge.hint)}
      </Text>

      {event && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "600",
            color: EMBER.colors.warm,
            textAlign: "center",
            marginTop: 12,
          }}
        >
          {event.copy.milestone}
        </Text>
      )}

      {badge.unlocked && badge.unlockedAt && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textFaint,
            marginTop: 12,
          }}
        >
          {t("you.my_passport.earned_on", {
            params: { date: formatDate(badge.unlockedAt) },
          })}
        </Text>
      )}

      {isNewlyUnlocked ? (
        <View
          style={{
            marginTop: 16,
            backgroundColor: `${EMBER.colors.warm}18`,
            borderRadius: 10,
            paddingHorizontal: 14,
            paddingVertical: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.warm,
              textAlign: "center",
            }}
          >
            {event?.copy.celebrationMode === "quiet"
              ? t("you.my_passport.new_in_passport")
              : (event?.copy.milestone ?? t("you.my_passport.newly_unlocked"))}
          </Text>
        </View>
      ) : !badge.unlocked ? (
        <View
          style={{
            marginTop: 16,
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 10,
            paddingHorizontal: 14,
            paddingVertical: 8,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textFaint,
              textAlign: "center",
            }}
          >
            {t("you.my_passport.complete_to_unlock")}
          </Text>
        </View>
      ) : null}

      {onViewInPassport && (
        <DialogPrimaryButton
          label={t("you.my_passport.view_in_passport")}
          onPress={onViewInPassport}
          style={{ marginTop: 20, alignSelf: "stretch" }}
          hapticStyle={celebrationEffectsEnabled ? "light" : null}
        />
      )}
    </>
  );
}

// ── Main component ──

export default function MyPassport({
  badges,
  badgeDataStatus,
  initialBadgeId,
  initialBadgeIds,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MyPassport");
  const { height: windowHeight } = useWindowDimensions();
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [arrivedBadges, setArrivedBadges] = useState<Badge[]>([]);
  const openedInitialBadgeIdsRef = useRef<string | null>(null);
  const openedInitialBadgeRef = useRef<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const unlockedCount = badges.filter((b) => b.unlocked).length;
  const passportScrollRef = useRef<ScrollView>(null);
  const passportContentWidthRef = useRef(0);
  const passportViewportWidthRef = useRef(0);
  const passportScrollOffsetRef = useRef(0);
  const passportScrollDirectionRef = useRef(1);
  const passportPausedUntilRef = useRef(0);
  const passportAutoScrollIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  // Track which badges were unlocked this session for animation
  const initialUnlockedRef = useRef<Set<string> | null>(null);
  if (initialUnlockedRef.current === null) {
    initialUnlockedRef.current = new Set(
      badges.filter((b) => b.unlocked).map((b) => b.id),
    );
  }
  const newlyUnlocked = new Set(
    badges
      .filter((b) => b.unlocked && !initialUnlockedRef.current!.has(b.id))
      .map((b) => b.id),
  );

  /**
   * Arriving from a celebration that held several badges.
   *
   * "View in passport" used to carry one id, so a patient who had just been
   * congratulated for three badges landed on one of them and the other two
   * were gone — she had seen them a second earlier and had no way back. The
   * link now carries every id it celebrated, and they open as the same
   * carousel she was already swiping.
   */
  useEffect(() => {
    if (
      !initialBadgeIds ||
      openedInitialBadgeIdsRef.current === initialBadgeIds ||
      badgeDataStatus !== "ready"
    ) {
      return;
    }

    const wanted = initialBadgeIds.split(",").filter(Boolean);
    const found = wanted.flatMap((id) => {
      const badge = badges.find(
        (candidate) => candidate.id === id && candidate.unlocked,
      );
      return badge ? [badge] : [];
    });

    // Marked only once something was found, mirroring the single-badge effect
    // below. Claiming the param on an empty result would leave the patient on a
    // silently empty passport with no retry when the badges finish loading.
    if (found.length === 0) return;

    openedInitialBadgeIdsRef.current = initialBadgeIds;
    // One survivor is a detail panel, not a carousel — the same fallback as a
    // link that only ever named one badge.
    if (found.length > 1) setArrivedBadges(found);
    else setSelectedBadge(found[0] ?? null);
  }, [badgeDataStatus, badges, initialBadgeIds]);

  useEffect(() => {
    if (
      !initialBadgeId ||
      openedInitialBadgeRef.current === initialBadgeId ||
      badgeDataStatus !== "ready"
    ) {
      return;
    }

    const badge = badges.find(
      (candidate) => candidate.id === initialBadgeId && candidate.unlocked,
    );
    if (badge) {
      openedInitialBadgeRef.current = initialBadgeId;
      setSelectedBadge(badge);
    }
  }, [badges, badgeDataStatus, initialBadgeId]);

  useEffect(() => {
    const startDelay = setTimeout(() => {
      passportAutoScrollIntervalRef.current = setInterval(() => {
        const maxOffset = Math.max(
          0,
          passportContentWidthRef.current - passportViewportWidthRef.current,
        );

        if (maxOffset <= 0 || passportPausedUntilRef.current > Date.now()) {
          return;
        }

        const nextOffset =
          passportScrollOffsetRef.current +
          passportScrollDirectionRef.current * 0.8;

        if (nextOffset >= maxOffset) {
          passportScrollDirectionRef.current = -1;
          passportScrollOffsetRef.current = maxOffset;
        } else if (nextOffset <= 0) {
          passportScrollDirectionRef.current = 1;
          passportScrollOffsetRef.current = 0;
        } else {
          passportScrollOffsetRef.current = nextOffset;
        }

        passportScrollRef.current?.scrollTo({
          x: passportScrollOffsetRef.current,
          animated: false,
        });
      }, 50);
    }, 1200);

    return () => {
      clearTimeout(startDelay);
      if (passportAutoScrollIntervalRef.current) {
        clearInterval(passportAutoScrollIntervalRef.current);
      }
    };
  }, []);

  const pausePassportAutoScroll = () => {
    passportPausedUntilRef.current = Date.now() + 4000;
  };

  const dialogMaxHeight = Math.max(360, windowHeight - 48);
  const dialogGridMaxHeight = Math.max(220, dialogMaxHeight - 150);

  if (badgeDataStatus === "loading") {
    return <PassportLoadingSkeleton t={t} />;
  }

  if (badgeDataStatus === "unavailable") {
    return <PassportUnavailableCard t={t} />;
  }

  return (
    <View style={{ marginBottom: 20 }}>
      {/* Header with View all */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <SectionHeader style={{ marginBottom: 0 }}>
            {t("you.my_passport.header")}
          </SectionHeader>
          <SectionHeader style={{ marginBottom: 0 }}>
            {`${unlockedCount}/${badges.length}`}
          </SectionHeader>
        </View>
        <ViewAllLink onPress={() => setShowAll(true)} />
      </View>

      {/* Inline horizontal scroll */}
      <View style={GLASS_CARD_STYLE}>
        <ScrollView
          ref={passportScrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          onContentSizeChange={(width) => {
            passportContentWidthRef.current = width;
          }}
          onLayout={({ nativeEvent }) => {
            passportViewportWidthRef.current = nativeEvent.layout.width;
          }}
          onScroll={(event) => {
            passportScrollOffsetRef.current = event.nativeEvent.contentOffset.x;
          }}
          onScrollBeginDrag={pausePassportAutoScroll}
          onTouchStart={pausePassportAutoScroll}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingVertical: 16,
            alignItems: "center",
          }}
        >
          {badges.map((badge) => (
            <InlineSeal
              key={badge.id}
              badge={badge}
              isNewlyUnlocked={newlyUnlocked.has(badge.id)}
              onPress={() => setSelectedBadge(badge)}
            />
          ))}
        </ScrollView>
      </View>

      {/* Detail panel for single badge */}
      <BadgeDetailPanel
        badge={selectedBadge}
        visible={selectedBadge !== null}
        onClose={() => setSelectedBadge(null)}
      />

      {/* Several badges arrived together from a celebration */}
      <BadgeUnlockCarouselDialog
        badges={arrivedBadges.map((badge) => ({ badge }))}
        visible={arrivedBadges.length > 1}
        onClose={() => setArrivedBadges([])}
        /**
         * No celebration here. She has already been congratulated and has come
         * back to look; the seal firing a haptic per card would make the
         * quieter screen the louder one.
         */
        celebrationEffectsEnabled={false}
      />

      {/* View All dialog */}
      <Dialog
        visible={showAll}
        onClose={() => setShowAll(false)}
        closeButton="minimal"
        containerStyle={{
          width: "92%",
          maxWidth: 420,
          maxHeight: dialogMaxHeight,
        }}
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{ padding: 0, maxHeight: dialogMaxHeight }}
      >
        <View style={{ maxHeight: dialogMaxHeight }}>
          {/* Header */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 20,
              paddingRight: 56,
              paddingTop: 20,
              paddingBottom: 16,
              borderBottomWidth: 1,
              borderBottomColor: EMBER.colors.border,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 18,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {t("you.my_passport.header")}
            </Text>
          </View>

          {/* Progress */}
          <View
            style={{ paddingHorizontal: 20, paddingTop: 16, paddingBottom: 8 }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                marginBottom: 8,
              }}
            >
              {t("you.my_passport.badges_collected", {
                params: {
                  unlocked: String(unlockedCount),
                  total: String(badges.length),
                },
              })}
            </Text>
            <View
              style={{
                width: "100%",
                height: 4,
                borderRadius: 2,
                backgroundColor: EMBER.colors.border,
              }}
            >
              <View
                style={{
                  width: `${(unlockedCount / badges.length) * 100}%`,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: EMBER.colors.warm,
                }}
              />
            </View>
          </View>

          {/* Grid */}
          <ScrollView
            style={{ maxHeight: dialogGridMaxHeight, flexShrink: 1 }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              padding: 20,
              paddingTop: 12,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {badges.map((badge) => {
              const unlocked = badge.unlocked;
              const gridSize = 82;

              return (
                <Pressable
                  key={badge.id}
                  onPress={() => {
                    capturePress(pressLabel(badge.name), "badge_grid_item");
                    setShowAll(false);
                    setTimeout(() => setSelectedBadge(badge), 220);
                  }}
                  style={{
                    width: "30%",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <View
                    style={{
                      width: gridSize,
                      height: gridSize,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Image
                      source={getBadgeImage(badge.id)}
                      style={{
                        width: gridSize,
                        height: gridSize,
                        position: "absolute",
                        ...(unlocked
                          ? {}
                          : Platform.OS === "web"
                            ? {
                                opacity: 0.5,
                                filter: "grayscale(1) brightness(1.1)",
                              }
                            : { opacity: 0.3 }),
                      }}
                      resizeMode="contain"
                    />
                  </View>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      fontWeight: unlocked ? "600" : "400",
                      color: unlocked
                        ? EMBER.colors.text
                        : EMBER.colors.textFaint,
                      textAlign: "center",
                      marginTop: 4,
                    }}
                  >
                    {badge.name}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Dialog>
    </View>
  );
}
